import { useLayoutEffect, useState, type RefObject } from 'react';

/**
 * @param textRef - The reference attach to the text ellipsis component
 * @param itemName - The item name without cropping.
 * @param width - the width of the container
 * @param height - the height of the container
 * @returns true or false depending whether name is truncated or not.
 */
export const useTruncationDetector = (
  textRef: RefObject<HTMLSpanElement | null>,
  itemName: string,
  width?: number,
  height?: number,
): boolean => {
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    if (textRef.current) {
      const isMiddleTruncation = checkMiddleTruncation(textRef.current);

      const isCssTruncation = checkCssTruncation(textRef.current);

      setIsTruncated(isMiddleTruncation || isCssTruncation);
    }
  }, [itemName, textRef, width, height]);

  return isTruncated;
};

/**
 * Detects if the text is truncated (middle) using javascript.
 * @param targetElement - The target element to check for middle truncation.
 * @returns true if the text is truncated, false otherwise.
 */
const checkMiddleTruncation = (targetElement: HTMLSpanElement) => {
  const spanElement = targetElement.querySelector('span');
  if (spanElement) {
    return spanElement.scrollWidth > spanElement.clientWidth;
  }
};

/**
 *  Detects if the text is truncated (start/end) using CSS ellipsis by checking the parent element's styles.
 * @param targetElement - The target element to check for CSS ellipsis truncation.
 * @returns true if the text is truncated, false otherwise.
 */
const checkCssTruncation = (targetElement: HTMLSpanElement) => {
  const parentStyle = window.getComputedStyle(targetElement);

  return !!(
    targetElement &&
    parentStyle?.textOverflow === 'ellipsis' &&
    parentStyle?.overflow === 'hidden' &&
    targetElement.scrollWidth > targetElement.clientWidth
  );
};
